import React, { useState } from 'react';
import { Modal, Button, TextInput, Stack, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { UuidString } from '~src/utils/UUID';
import { Group } from '~/src/types/groups';
import { peep_client } from '~src/api/peep';
import ProfileImageUploader from './ProfileImageUploader';

interface EditProfileModalProps {
  opened: boolean;
  onClose: () => void;
  group: Group;
  currentAlias: string;
  currentProfileImage: string | null;
}

type FormState =
  | { status: 'editing'; alias: string }
  | { status: 'submitting'; alias: string }
  | {
      status: 'error';
      alias: string;
      error: string;
    };

export default function EditProfileModal({
  opened,
  onClose,
  group,
  currentAlias,
  currentProfileImage,
}: EditProfileModalProps): JSX.Element {
  const [state, setState] = useState<FormState>({
    status: 'editing',
    alias: currentAlias,
  });

  const handleImageUploaded = (uploadId: UuidString): void => {
    notifications.show({
      title: 'Profile Image Updated',
      message: 'Your profile image has been updated successfully',
      color: 'green',
    });
    onClose();
  };

  const handleSubmit = async (): Promise<void> => {
    if (state.status !== 'editing') {
      return;
    }

    setState({ ...state, status: 'submitting' });

    try {
      await peep_client.groups.updateProfile({
        group_id: group.id,
        alias: state.alias !== currentAlias ? state.alias : undefined,
      });

      notifications.show({
        title: 'Profile Updated',
        message: 'Your profile has been updated successfully',
        color: 'green',
      });

      onClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update profile';
      setState({
        status: 'error',
        alias: state.alias,
        error: errorMessage,
      });
      notifications.show({
        title: 'Update Failed',
        message: errorMessage,
        color: 'red',
      });
    }
  };

  const uploadFunction = async (
    file: File,
    onProgress: (progress: number | undefined) => void,
    signal: AbortSignal,
  ): Promise<UuidString> => {
    return await peep_client.uploads.upload_profile_image({ file, group_id: group.id }, onProgress, signal);
  };

  const hasChanges = (state.status === 'editing' || state.status === 'error') && state.alias !== currentAlias;

  return (
    <Modal opened={opened} onClose={onClose} title="Edit My Profile" centered>
      <Stack gap="md">
        <ProfileImageUploader
          currentImageUrl={currentProfileImage}
          seedId={group.id.toString()}
          onImageUploaded={handleImageUploaded}
          uploadFunction={uploadFunction}
        />

        <TextInput
          label="Display Name"
          description="This is what you will show up as to other users in this group"
          value={state.status !== 'submitting' ? state.alias : state.alias}
          onChange={(e) => {
            if (state.status === 'editing' || state.status === 'error') {
              setState({ ...state, alias: e.target.value, status: 'editing' });
            }
          }}
          disabled={state.status === 'submitting'}
        />

        <Button
          onClick={handleSubmit}
          disabled={state.status === 'submitting' || !hasChanges || state.alias.trim() === ''}
          loading={state.status === 'submitting'}
        >
          Save Changes
        </Button>
      </Stack>
    </Modal>
  );
}
